import React from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { filter, isEmpty } from 'lodash';
import map from 'lodash/map';
import { useSelector } from 'react-redux';

import { StackedHeader } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { MonitoringIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { seletMaskingPolicies } from 'containers/Jit/MaskingPolicies/selectors';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: Record<string, any>;
}

/** `rows` gives a key and a name for each line. Two identities can have the
 * same name, so the key must not be the name. */
const renderSection = (title: string, rows?: { key: string; name: string }[]) =>
  isEmpty(rows) ? null : (
    <Stack spacing={1}>
      <Box fontWeight={600}>{title}</Box>
      {map(rows, (row, index) => (
        <HStack key={`${title}-${row.key}`}>
          <Box>{index + 1}.</Box>
          <Box>{row.name}</Box>
        </HStack>
      ))}
    </Stack>
  );

export const OktaIdentitiesUsingRoleModal = (props: Props) => {
  const { isOpen, onClose, role } = props;
  const { identitiesUsingDataRole } = useSelector(seletMaskingPolicies);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          icon={<MonitoringIcon />}
          upper="Okta Identities Using Role"
          lower={role?.name}
        />
      }
      body={
        <Stack>
          {identitiesUsingDataRole.isLoading ? (
            <Box h={10}>
              <Center>
                <CustomSpinner />
              </Center>
            </Box>
          ) : isEmpty(identitiesUsingDataRole.data) &&
            isEmpty(role?.permissions) ? (
            <Box h={10}></Box>
          ) : (
            <Stack spacing={4}>
              {renderSection(
                'Okta Groups',
                map(
                  filter(
                    identitiesUsingDataRole.data,
                    o => o.resource_type === NativeResources.OktaGroup,
                  ),
                  o => ({ key: o.uuid, name: o.name }),
                ),
              )}
              {renderSection(
                'Okta Users',
                map(
                  filter(
                    identitiesUsingDataRole.data,
                    o => o.resource_type === NativeResources.OktaUser,
                  ),
                  o => ({ key: o.uuid, name: o.name }),
                ),
              )}
              {renderSection(
                'Permissions',
                map(role?.permissions, p => ({ key: p, name: p })),
              )}
            </Stack>
          )}
        </Stack>
      }
    />
  );
};
